import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast as sonnerToast } from 'sonner';

import type { IconVariant } from '~/chords/Icon';
import { Icon } from '~/chords/Icon';
import { IconButton } from '~/chords/IconButton';

export type ToastType = 'error' | 'success' | 'info' | 'warning' | 'loading';

const TypePropsMap: Record<
  ToastType,
  {
    iconType: string;
    iconClassName: string;
    iconVariant?: IconVariant;
    labelKey: string;
    liveAttr: 'assertive' | 'polite';
    role: 'alert' | 'status';
  }
> = {
  error: {
    iconType: 'octagon-xmark',
    iconClassName: 'text-error-default',
    labelKey: 'error',
    liveAttr: 'assertive',
    role: 'alert',
  },
  success: {
    iconType: 'check-circle',
    iconClassName: 'text-success-default',
    labelKey: 'success',
    liveAttr: 'polite',
    role: 'status',
  },
  info: {
    iconType: 'circle-info',
    iconClassName: 'text-info-default',
    labelKey: 'info',
    liveAttr: 'polite',
    role: 'status',
  },
  warning: {
    iconType: 'circle-exclamation',
    iconClassName: 'text-warning-default',
    labelKey: 'warning',
    liveAttr: 'polite',
    role: 'status',
  },
  loading: {
    iconType: 'spinner-third',
    iconClassName: 'fa-spin',
    iconVariant: 'duotone',
    labelKey: 'loading',
    liveAttr: 'polite',
    role: 'status',
  },
};

export interface ToastProps {
  id: string | number;
  title: string;
  description?: ReactNode;
  onClose?: () => void;
  type?: ToastType;
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export function Toast({
  title,
  description,
  id,
  onClose = () => {},
  type = 'error',
}: ToastProps) {
  const { t } = useTranslation('components', { keyPrefix: 'toast' });

  const { iconType, iconClassName, iconVariant, labelKey, liveAttr, role } =
    TypePropsMap[type];

  return (
    <div
      className="w-full min-w-[275px] rounded-sm px-md py-sm shadow-lg bg-level-05 border-emphasis-subtle text-default md:max-w-[364px]"
      role={role}
      aria-live={liveAttr}
      aria-label={t(`ariaLabels.${labelKey}`)}
    >
      <div className="flex flex-col gap-sm">
        <div className="flex justify-between">
          <div className="flex items-center gap-sm">
            <Icon
              type={iconType}
              dangerouslyOverrideClassName={{ __className: iconClassName }}
              variant={iconVariant}
              size="md"
              data-testid="toast-icon"
            />
            <div className="text-headline-sm font-bold">{title}</div>
          </div>
          <div className="-mr-sm shrink-0 flex-grow-0 basis-auto p-xs">
            <IconButton
              iconType="xmark"
              onClick={() => {
                sonnerToast.dismiss(id);
                onClose();
              }}
              aria-label={t('close')}
            />
          </div>
        </div>
        {description ? (
          <div className="text-body-sm font-normal">{description}</div>
        ) : null}
      </div>
    </div>
  );
}

Toast.displayName = 'Toast';
